<template>
  <div class="mb-[-64px] pr-24 pt-24 text-right">
    <RevButton
      :loading="allListingsAreNotSynched"
      size="small"
      variant="primary"
      @click="handleWinAllBackbox"
    >
      {{
        i18n(
          shouldShowBackboxPrice
            ? translations.winAllBackbox
            : translations.updateAllPrices,
        )
      }}
    </RevButton>
  </div>

  <RevTable
    v-model:sort-options="sortOptions"
    :caption
    :collection
    :columns
    transparent-header
  >
    <template #body-listingAndSku="{ item }">
      {{ item.title }}<br />
      {{ item.sku }}
    </template>

    <template #body-grade="{ item }">
      <RevSpinner v-if="item.isUnsynched" />
      <template v-else>
        <RevTag :label="item.backboxGrade.label" variant="secondary" />
      </template>
    </template>

    <template #body-market="{ item }">
      <div class="flex items-center justify-between">
        <Flag class="mr-8" :country-code="item.market" size="l" />
        {{ item.market }}
      </div>
    </template>

    <template #body-backbox="{ item }">
      <IconCheckInCircle
        v-if="item.hasBackbox"
        class="text-static-success-hi"
      />
      <IconCrossInCircle
        v-if="!item.hasBackbox"
        class="text-static-danger-hi"
      />
    </template>

    <template #body-yourPrice="{ item }">
      <RevSpinner v-if="item.isUnsynched" />
      <template v-else>
        {{ i18n.price(item.price) }}
      </template>
    </template>

    <template #body-backboxPrice="{ item }">
      <RevSpinner v-if="item.isUnsynched" />
      <template v-else>
        {{ i18n.price(item.targetPrice) }}
      </template>
    </template>

    <template #body-recommendedPrice="{ item }">
      <RevSpinner v-if="item.isUnsynched" />
      <template v-else>
        {{ i18n.price(item.recommendedPrice) }}
      </template>
    </template>

    <template #body-priceDifference="{ item }">
      <RevSpinner v-if="item.isUnsynched" />
      <template v-else>
        {{ i18n.price(item.priceDifference) }}

        <span class="body-2 ml-4">
          {{
            i18n(translations.priceDifferencePercentage, {
              percentage: item.priceDifference.pricePercentDifference,
            })
          }}
        </span>
      </template>
    </template>

    <template #body-actions="{ item }">
      <div class="flex flex-col space-y-8">
        <RevButton
          v-if="shouldShowBackboxPrice"
          :loading="item.isUnsynched"
          size="small"
          variant="primary"
          @click="handleWinBackbox(item)"
        >
          {{ i18n(translations.winBackbox) }}
        </RevButton>

        <RevButton
          v-if="!props.shouldShowBackboxPrice"
          :loading="item.isUnsynched"
          size="small"
          variant="primary"
          @click="handleUpdatePrice(item)"
        >
          {{ i18n(translations.updatePrice) }}
        </RevButton>

        <RevButton
          size="small"
          :to="{
            type: 'internal',
            name: isListingsPageV2Default
              ? ROUTE_NAMES.LISTINGSV2.INDEX
              : ROUTE_NAMES.LISTINGS.INDEX,
            query: { sku: item.sku },
          }"
          :tracking="{
            name: 'see-listing',
            page_type: 'opportunities_pricing',
            zone: amplitudeOpportunityTypeName,
            product_listing_id: item.listingId,
          }"
          variant="secondary"
        >
          {{ i18n(translations.seeListing) }}
        </RevButton>
      </div>
    </template>
  </RevTable>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage, useRoute } from '#imports'
import { computed, ref, watch } from 'vue'

import { sellerOpportunities } from '@backmarket/http-api'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { insertIf } from '@backmarket/utils/collection/insertIf'
import { RevButton } from '@ds/components/Button'
import { RevSpinner } from '@ds/components/Spinner'
import {
  RevTable,
  type SortOptions,
  type TableSortOrder,
} from '@ds/components/Table'
import { RevTag } from '@ds/components/Tag'
import { IconCheckInCircle } from '@ds/icons/IconCheckInCircle'
import { IconCrossInCircle } from '@ds/icons/IconCrossInCircle'

import { useListingPriceUpdater } from '~/composables/useListingPriceUpdater'
import { ROUTE_NAMES } from '~/constants/routes'
import { useListingPagePreference } from '~/scopes/listings/pages/listingsV2/composables/useListingPagePreference'

import translations from './OpportunityTable.translations'

const props = withDefaults(
  defineProps<{
    amplitudeOpportunityTypeName: string
    caption: string
    opportunities: sellerOpportunities.PricingOpportunity[]
    shouldShowBackboxPrice: boolean
  }>(),
  {
    shouldShowBackboxPrice: true,
  },
)

const { isListingsPageV2Default } = useListingPagePreference()

const emit = defineEmits(['sort-options-changed', 'on-update'])

const { updateListingWithOpportunityPrice, winBackboxForOpportunity } =
  await useListingPriceUpdater()
const { trackClick } = useTracking()
const route = useRoute()
const { openErrorToast, openSuccessToast } = useTheToast()
const i18n = useI18n()

const orderBy = (route.query?.orderBy as string) || 'priceDifference:UNDEFINED'
const [sortBy, sortOrder] = orderBy.split(':')

const sortOptions = ref<SortOptions>({
  sortBy: sortBy || '',
  sortOrder: (sortOrder.toUpperCase() as TableSortOrder) || 'UNDEFINED',
})

const collection = props.opportunities
const columns = [
  { key: 'listingAndSku', label: i18n(translations.listingAndSku) },
  { key: 'categoryName', label: i18n(translations.itemCategory) },
  { key: 'grade', label: i18n(translations.grade) },
  { key: 'market', label: i18n(translations.market) },
  { key: 'inventory', label: i18n(translations.inventory), isTabular: true },
  { key: 'backbox', label: i18n(translations.backbox) },
  { key: 'yourPrice', label: i18n(translations.yourPrice), isTabular: true },
  ...insertIf(props.shouldShowBackboxPrice, [
    {
      key: 'backboxPrice',
      label: i18n(translations.backboxPrice),
      isTabular: true,
      highlighted: true,
    },
  ]),
  ...insertIf(!props.shouldShowBackboxPrice, [
    {
      key: 'recommendedPrice',
      label: i18n(translations.recommendedPrice),
      isTabular: true,
      highlighted: true,
    },
  ]),
  {
    key: 'priceDifference',
    label: i18n(translations.priceDifference),
    isTabular: true,
    isSortable: true,
  },
  {
    key: 'actions',
    label: '',
  },
]

async function handleWinBackbox(
  opportunity: sellerOpportunities.PricingOpportunity,
) {
  trackClick({
    name: 'win-backbox',
    page_type: 'opportunities_pricing',
    zone: props.amplitudeOpportunityTypeName,
    listing_id: opportunity.listingId,
    listing_title: opportunity.title,
    current_listing_price: opportunity.price?.amount,
    price_to_win: opportunity.targetPrice?.amount,
    price_difference: opportunity.priceDifference?.amount,
    recommended_price: opportunity.recommendedPrice?.amount,
  })

  try {
    await winBackboxForOpportunity(opportunity)

    emit('on-update')

    openSuccessToast({
      content: i18n(translations.listingUpdated),
    })
  } catch (error) {
    openErrorToast({
      content: getApiErrorMessage(error as unknown as Error),
    })
  }
}

async function handleUpdatePrice(
  opportunity: sellerOpportunities.PricingOpportunity,
) {
  trackClick({
    name: 'update-price',
    page_type: 'opportunities_pricing',
    zone: props.amplitudeOpportunityTypeName,
    product_listing_id: opportunity.listingId,
  })

  try {
    await updateListingWithOpportunityPrice(opportunity)

    emit('on-update')

    openSuccessToast({
      content: i18n(translations.listingUpdated),
    })
  } catch (error) {
    openErrorToast({
      content: getApiErrorMessage(error as unknown as Error),
    })
  }
}

const allListingsAreNotSynched = computed(() => {
  return props.opportunities.every((opportunity) => opportunity.isUnsynched)
})

function displaySuccessMessageForBulkActions(
  updatedResults: { status: 'fulfilled' | 'rejected' }[],
) {
  const numberOfSuccess = updatedResults.filter(
    (result) => result.status === 'fulfilled',
  ).length

  if (numberOfSuccess > 0) {
    openSuccessToast({
      content: i18n(translations.successOnBulkActions, {
        count: numberOfSuccess,
      }),
    })
  }
}

function getErrorMessageForBulkActions(
  updatedResults: { status: 'fulfilled' | 'rejected' }[],
) {
  const listingsThatFailed: string[] = []

  updatedResults.forEach((result, indexResult) => {
    if (result.status === 'rejected') {
      listingsThatFailed.push(props.opportunities[indexResult].title)
    }
  })

  let errorMessage = ''

  if (listingsThatFailed.length) {
    errorMessage = i18n(translations.errorOnBulkActions, {
      listings: listingsThatFailed.join(', '),
    })
  }

  return errorMessage
}

async function handleWinAllBackbox() {
  try {
    trackClick({
      name: props.shouldShowBackboxPrice
        ? 'win-all-backboxes'
        : 'update-all-prices',
      page_type: 'opportunities_pricing',
      zone: props.amplitudeOpportunityTypeName,
      product_listing_id: props.opportunities
        .map((opportunity) => opportunity.listingId)
        .join(';'),
    })

    const listingsToUpdate = props.opportunities.map((opportunity) =>
      props.shouldShowBackboxPrice
        ? winBackboxForOpportunity(opportunity)
        : updateListingWithOpportunityPrice(opportunity),
    )

    const updatedResults = await Promise.allSettled(listingsToUpdate)

    displaySuccessMessageForBulkActions(updatedResults)

    emit('on-update', {
      errorMessage: getErrorMessageForBulkActions(updatedResults),
    })
  } catch (error) {
    openErrorToast({
      content: getApiErrorMessage(error as Error),
    })
  }
}

watch(sortOptions, (newSortOption) => {
  emit('sort-options-changed', newSortOption)
})
</script>
